/**
 * Core Web Vitals reporting to Google Analytics 4.
 *
 * Uses the `web-vitals` library (already in the dependency tree of most modern
 * React setups; if not, add via `pnpm add web-vitals`). Metrics are only
 * dispatched to GA4 AFTER the user has granted analytics consent — so consent
 * is honored end to end. If gtag hasn't loaded yet, we buffer via dataLayer,
 * which is safe: gtag's async loader will drain any queued events.
 *
 * GA4 automatically buckets these events under "Web Vitals" once at least one
 * event is received. Event schema follows the recommended shape:
 *   event: <metric-name-lowercase>  (e.g. "lcp", "cls", "inp")
 *   parameters:
 *     value:       rounded numeric metric value in ms (CLS is *1000 for GA4)
 *     metric_id:   the unique id from web-vitals
 *     metric_rating: "good" | "needs-improvement" | "poor"
 *     metric_value_raw: the raw metric value (unrounded)
 *     page_path:   current pathname
 */

const GA_ID = "G-P568VWDRRY";

type Metric = {
  name: "CLS" | "INP" | "LCP" | "FCP" | "TTFB";
  value: number;
  id: string;
  rating: "good" | "needs-improvement" | "poor";
};

function report(metric: Metric): void {
  if (typeof window === "undefined") return;
  const dataLayer = (window as any).dataLayer;
  if (!Array.isArray(dataLayer)) return;

  // GA4 expects CLS multiplied by 1000 to fit its integer buckets.
  const roundedValue =
    metric.name === "CLS"
      ? Math.round(metric.value * 1000)
      : Math.round(metric.value);

  dataLayer.push({
    event: metric.name.toLowerCase(),
    send_to: GA_ID,
    value: roundedValue,
    metric_id: metric.id,
    metric_rating: metric.rating,
    metric_value_raw: metric.value,
    page_path: window.location.pathname,
  });
}

/**
 * Initialize CWV reporting. Call once from the app entry, after the app has
 * hydrated. Safe to call before consent is granted — the metrics still buffer
 * into dataLayer, and Consent Mode will drop them if consent stays denied.
 */
export async function initWebVitals(): Promise<void> {
  // Bots, prerender, and headless renders shouldn't report.
  if (typeof navigator === "undefined") return;
  if (navigator.webdriver) return;
  const ua = (navigator.userAgent || "").toLowerCase();
  if (
    ua.includes("headlesschrome") ||
    ua.includes("puppeteer") ||
    ua.includes("googlebot") ||
    ua.includes("bingbot")
  ) {
    return;
  }

  try {
    // Dynamic import so web-vitals doesn't block initial parse/hydrate.
    const mod = await import("web-vitals");
    mod.onCLS(report);
    mod.onLCP(report);
    mod.onINP(report);
    mod.onFCP(report);
    mod.onTTFB(report);
  } catch {
    // web-vitals not installed or failed to load. Silent — this is telemetry.
  }
}
